import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import logger from '@adc/logger';
import analytics from '@adc/analytics';
import classnames from 'classnames/bind';
import { match } from 'ts-pattern';
import * as graph from '@adc/sdk';
import Button from '../../elementals/button';
import Icon from '../../elementals/icon';
import Text from '../../elementals/text';
import useBreakpoints from '../../../hooks/useBreakpoints';
import { ListingInfoCardRibbon } from './listingInfoCardRibbon';
import Overlay from './overlay';
import styles from './index.scss';
import { VerifiedImage, BackgroundImage, SecondaryItem, MapItem, TABS, } from './parts';
import ImageWatermark from './overlay/imageWatermark';
import getStreetViewAvailability from '../../../utils/getStreetViewAvailability';
const cx = classnames.bind(styles);
export const LISTING_INFORMATION_PHOTO_WIDTH = 955;
export var AlternateItemType;
(function (AlternateItemType) {
    AlternateItemType[AlternateItemType["MapItem"] = 0] = "MapItem";
    AlternateItemType[AlternateItemType["StreetViewItem"] = 1] = "StreetViewItem";
    AlternateItemType[AlternateItemType["CustomItem"] = 2] = "CustomItem";
})(AlternateItemType || (AlternateItemType = {}));
const getCommonTrackingValues = (listing) => {
    const { morpheusListingId, morpheusPropertyId } = analytics.standardTracking.listing(listing);
    return {
        morpheusListingId,
        morpheusPropertyId,
    };
};
const trackEvent = (event, listing) => {
    analytics.track(event, getCommonTrackingValues(listing));
};
const defaultAlternateItems = [
    {
        itemType: AlternateItemType.MapItem,
        showAlways: true,
    },
    {
        itemType: AlternateItemType.StreetViewItem,
        showAlways: true,
    },
    {
        itemType: AlternateItemType.CustomItem,
        showAlways: true,
        renderer: () => (_jsx("div", { style: {
                borderRadius: 6,
                display: 'flex',
                background: 'lightgrey',
                height: '100%',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
            }, children: _jsx(Text, { color: 'gray', weight: 'medium', children: "Custom Item Goes Here (TODO)" }) })),
    },
];
const PhotoGallery = ({ config, className, cls = {}, displayBanner = false, shouldShowRibbon = false, children, featureFlags = {}, listing, fluid = false, showMultiple = false, alternativeItems = defaultAlternateItems, supportLargeDesktop = true, watermarkType, defaultPhotoConfigKey, }) => {
    const [images, setImages] = useState([]);
    const breakpoint = useBreakpoints();
    const isMobile = breakpoint === 'mobile';
    useEffect(() => {
        if (!listing?.photos?.length) {
            setImages([]);
        }
        else {
            setImages(listing?.photos
                ?.map(photo => photo?.file_descriptor?.urls?.cdn)
                .filter((url) => !!url));
        }
    }, [listing.photos]);
    const addressTuple = useMemo(() => listing.formatted_address?.filter((address) => !!address) || ['', ''], [listing.formatted_address]);
    const carouselRef = useRef(null);
    const [activeTabIndex, setActiveTabIndex] = useState(TABS.NONE);
    const [streetViewAvailable, setStreetViewAvailable] = useState(false);
    const coords = useMemo(() => {
        const { lat, lon } = listing?.primary_property?.summary?.address?.coordinates || {};
        if (lat !== undefined &&
            lat !== null &&
            lon !== undefined &&
            lon !== null) {
            return { lat, lng: lon };
        }
        return undefined;
    }, [listing?.primary_property?.summary?.address?.coordinates]);
    const toggleLock = (open = true) => {
        if (typeof window === 'undefined')
            return;
        const method = open ? 'add' : 'remove';
        document.documentElement.classList[method](cx('body-noscroll'));
    };
    useEffect(() => {
        ;
        (async () => {
            if (coords) {
                try {
                    const available = await getStreetViewAvailability(coords);
                    setStreetViewAvailable(available);
                }
                catch (error) {
                    logger.info('Unhandled error in StreetView availability check:', error);
                    setStreetViewAvailable(false);
                }
            }
        })().catch(error => {
            logger.info('Unhandled error in StreetView availability check:', error);
        });
    }, [coords]);
    const setActiveTab = useCallback((activeTabIndex) => {
        switch (activeTabIndex) {
            case TABS.PHOTOS:
                trackEvent('pdp-gallery-photos-button', listing);
                break;
            case TABS.STREET:
                trackEvent('pdp-gallery-street-button', listing);
                break;
            case TABS.MAP:
                trackEvent('pdp-gallery-map-button', listing);
                break;
            default:
                break;
        }
        setActiveTabIndex(activeTabIndex);
        toggleLock(true);
    }, []);
    const onClose = useCallback(() => {
        setActiveTabIndex(null);
        toggleLock(false);
    }, []);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
        if (activeTabIndex === null)
            return;
        if (event.key === 'Escape') {
            onClose();
        }
    }, [activeTabIndex, onClose]);
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);
    const statusLabelRole = useMemo(() => {
        return match(listing)
            .with({ is_coming_soon: true }, () => ({
            color: 'success',
            icon: 'house',
        }))
            .with({ listing_status_group: graph.enums.ListingStatusGroup.Active }, () => ({ color: 'success', icon: 'gavel' }))
            .with({ listing_status_group: graph.enums.ListingStatusGroup.Pending }, () => ({ color: 'warning', icon: 'house-circle-check' }))
            .with({ listing_status_group: graph.enums.ListingStatusGroup.Sold }, () => ({ color: 'warning', icon: 'house-circle-check' }))
            .with({ listing_status_group: graph.enums.ListingStatusGroup.Inactive }, () => ({
            color: 'warning',
            icon: 'house',
        }))
            .otherwise(() => ({ statusGroup: '', color: '', icon: '' }));
    }, [listing]);
    const videoUrlForThisProperty = listing.primary_property_id &&
        typeof featureFlags['fe.resi.property-video-config'] === 'object'
        ? featureFlags['fe.resi.property-video-config']?.[listing.primary_property_id]
        : undefined;
    const videoButtonClicked = () => {
        analytics.track({ eventName: 'video_button_clicked' });
    };
    const mapButton = useMemo(() => (_jsxs(Button, { icon: true, size: 'small', elmId: 'property_gallery_ctrl_map', onClick: () => setActiveTab(TABS.MAP), children: [_jsx(Icon, { name: 'map-marked', iconStyle: 'light', size: 'large' }), " Map"] })), [setActiveTab]);
    const wontDisplayMultiple = !showMultiple ||
        ((breakpoint === 'mobile' || breakpoint === 'tablet') && showMultiple);
    const renderButtons = useCallback(() => (_jsxs(_Fragment, { children: [_jsxs("div", { className: cx('left-button-group'), children: [images.length > 1 && (_jsxs(Button, { icon: true, size: 'small', elmId: 'property_gallery_ctrl_image', onClick: () => setActiveTab(TABS.PHOTOS), children: [_jsx(Icon, { name: 'image', iconStyle: 'light', size: 'large' }), ' ', images.length, " Photos"] })), videoUrlForThisProperty && (_jsx(Button, { icon: true, size: 'small', className: cx('ctrl-video'), elmId: 'property_gallery_ctrl_video', link: videoUrlForThisProperty, target: '_blank', onClick: videoButtonClicked, children: _jsx(Icon, { name: 'video', iconStyle: 'light', size: 'large' }) }))] }), coords && wontDisplayMultiple && (_jsxs("div", { className: cx('right-button-group'), children: [mapButton, streetViewAvailable && (_jsxs(Button, { icon: true, size: 'small', elmId: 'property_gallery_ctrl_street', onClick: () => setActiveTab(TABS.STREET), children: [_jsx(Icon, { name: 'street-view', iconStyle: 'light', size: 'large' }), ' ', "Street"] }))] }))] })), [
        coords,
        images.length,
        mapButton,
        setActiveTab,
        streetViewAvailable,
        videoUrlForThisProperty,
        wontDisplayMultiple,
    ]);
    const onSecondaryPhotoClick = useCallback((index, enabled) => {
        if (!enabled)
            return;
        setActiveTab(TABS.PHOTOS);
        setTimeout(() => {
            if (carouselRef.current) {
                carouselRef.current.slickGoTo(index);
            }
        }, 10);
    }, [setActiveTab]);
    const SecondaryPhoto = useCallback(({ index, url, featureFlags, className }) => (_jsx(BackgroundImage, { className: cx('secondary-photo', `index-${index}`, { pointer: Boolean(url) }, className), "data-elm-id": `secondary_photo_${index}`, onClick: () => onSecondaryPhotoClick(index, Boolean(url)), featureFlags: featureFlags, url: url, defaultPhotoConfigKey: defaultPhotoConfigKey })), [onSecondaryPhotoClick, defaultPhotoConfigKey]);
    const hasMap = Boolean(coords);
    const renderSecondaryPhotoOrPrimary = useCallback((correspondingImageIndex) => {
        const hasEnoughImages = images.length > correspondingImageIndex;
        if (hasEnoughImages)
            return (_jsx(SecondaryPhoto, { index: correspondingImageIndex, url: images[correspondingImageIndex], featureFlags: featureFlags }, correspondingImageIndex));
        return (_jsx(SecondaryPhoto, { index: 1, featureFlags: featureFlags }, correspondingImageIndex));
    }, [SecondaryPhoto, featureFlags, images]);
    const getItemElement = useCallback((item, index) => {
        return match(item)
            .with({ itemType: AlternateItemType.MapItem }, { itemType: AlternateItemType.StreetViewItem }, item => hasMap ? (_jsx(MapItem, { defaultView: item.itemType === AlternateItemType.StreetViewItem
                ? renderSecondaryPhotoOrPrimary(index)
                : undefined, index: index, coords: coords, setActiveTab: setActiveTab, showStreetView: item.itemType === AlternateItemType.StreetViewItem }, index)) : (renderSecondaryPhotoOrPrimary(index)))
            .with({ itemType: AlternateItemType.CustomItem }, item => {
            if (!item.renderer) {
                throw new Error('Custom item must have a renderer');
            }
            return (_jsx(SecondaryItem, { index: index, renderer: item.renderer }, index));
        })
            .otherwise(() => {
            throw new Error('Unknown item type');
        });
    }, [coords, hasMap, renderSecondaryPhotoOrPrimary, setActiveTab]);
    const getItemElements = useCallback((countNeeded) => {
        let position = countNeeded;
        const sourceAlternativeItems = [...alternativeItems];
        const newItemElements = [];
        let altItem = sourceAlternativeItems.shift();
        while (altItem && newItemElements.length < countNeeded) {
            if (altItem.showAlways || images.length <= position) {
                newItemElements.unshift(getItemElement(altItem, position));
            }
            else {
                newItemElements.unshift(renderSecondaryPhotoOrPrimary(position));
            }
            position--;
            altItem = sourceAlternativeItems.shift();
        }
        while (newItemElements.length < countNeeded) {
            newItemElements.unshift(renderSecondaryPhotoOrPrimary(position));
            position--;
        }
        return newItemElements;
    }, [
        alternativeItems,
        getItemElement,
        images.length,
        renderSecondaryPhotoOrPrimary,
    ]);
    const getSecondaryItems = useMemo(() => {
        const items = match(breakpoint)
            .with('mobile', () => [])
            .with('tablet', () => [])
            .with('laptop', () => getItemElements(2))
            .otherwise(() => getItemElements(4));
        return items;
    }, [breakpoint, getItemElements]);
    const PrimaryOverlay = ({ watermarkType, }) => (_jsx(_Fragment, { children: _jsxs(_Fragment, { children: [renderButtons(), isMobile && listing.listing_status_label && displayBanner && (_jsx("div", { className: cx('status-label', statusLabelRole.color), "data-elm-id": 'property_gallery_status_label', children: _jsx(Text, { color: 'white', weight: 'medium', children: listing.listing_status_label }) })), _jsx(ImageWatermark, { type: watermarkType })] }) }));
    const PrimaryPhoto = () => {
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: cx('image-container', { pointer: images.length > 0 }), children: _jsx(VerifiedImage, { className: cx(cls.image, 'image'), "data-elm-id": 'property_gallery_image', alt: `${addressTuple[0]} ${addressTuple[1]}`, onClick: images.length > 0 ? () => setActiveTab(TABS.PHOTOS) : () => null, url: images[0], featureFlags: featureFlags, defaultPhotoConfigKey: defaultPhotoConfigKey }) }), _jsx(PrimaryOverlay, { watermarkType: watermarkType })] }));
    };
    return (_jsxs(_Fragment, { children: [shouldShowRibbon && !isMobile && (_jsx(ListingInfoCardRibbon, { listing: listing, statusLabelRole: statusLabelRole })), _jsxs("div", { className: cx('root', className, { fluid, multiple: showMultiple }), "data-elm-id": 'property_gallery_section', children: [!showMultiple || breakpoint === 'mobile' ? (_jsx(PrimaryPhoto, {})) : (_jsxs("div", { className: cx('multi-container', {
                            'large-desktop-support': supportLargeDesktop,
                        }), children: [_jsxs("div", { className: cx('primary-photo', { pointer: images.length > 0 }), children: [_jsx(BackgroundImage, { className: cx('primary-photo'), "data-elm-id": 'property_gallery_image', onClick: images.length > 0
                                            ? () => setActiveTab(TABS.PHOTOS)
                                            : () => undefined, featureFlags: featureFlags, url: images?.[0] || undefined }, 0), _jsx(PrimaryOverlay, { watermarkType: watermarkType })] }), _jsx("div", { className: cx('secondary-photos'), children: getSecondaryItems })] })), activeTabIndex !== null && (_jsx(Overlay, { config: config, coords: coords, images: images, addressTuple: addressTuple, activeTabIndex: activeTabIndex, onTabChange: setActiveTab, onSave: () => undefined, onClose: onClose, featureFlags: featureFlags, carouselRef: carouselRef, children: children }))] })] }));
};
export default PhotoGallery;
//# sourceMappingURL=index.js.map